// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * The packer — trims a conversation until it fits a budget.
 *
 * Anyone can drop messages until a number goes down. The reason this is a
 * module with tests rather than a slice() is that the obvious implementation
 * hands back a conversation the API will reject:
 *
 *  - A `tool` result whose originating `tool_calls` message has been dropped is
 *    an orphan, and every provider rejects it. Assistant tool calls and their
 *    results are therefore treated as one atomic group and dropped together.
 *  - Dropping the system prompt changes the model's behaviour completely. It is
 *    protected by default, and when the budget is so small that it must go, the
 *    packer says so out loud rather than quietly removing it.
 *  - Dropping the last user turn leaves nothing to answer. It is always kept.
 *
 * The plan is made from measured per-message costs, and then the packed result
 * is re-rendered and re-counted for real, because the interaction between what
 * was dropped and what the template emits is not perfectly additive. The number
 * shown to the user is always the verified one, never the estimate.
 */

import type { ChatMessage, Segment } from './types';

export type PackStrategy = 'oldest-first' | 'documents-first';

export interface PackGroup {
  /** Indices into the original messages array, in order. */
  indices: number[];
  tokens: number;
  protectedReason: 'system' | 'final-turn' | null;
  retrieved: boolean;
  label: string;
}

/**
 * Group messages so that an assistant turn carrying `tool_calls` stays welded
 * to the `tool` results that answer it.
 */
export function buildGroups(messages: ChatMessage[], costOf: (index: number) => number): PackGroup[] {
  const groups: PackGroup[] = [];
  const lastUserIndex = findLastIndex(messages, (m) => String(m.role) === 'user');

  let i = 0;
  while (i < messages.length) {
    const m = messages[i];
    const indices = [i];

    if (String(m.role) === 'assistant' && Array.isArray(m.tool_calls) && m.tool_calls.length > 0) {
      let j = i + 1;
      while (j < messages.length && String(messages[j].role) === 'tool') {
        indices.push(j);
        j += 1;
      }
    }

    const isSystem = String(m.role) === 'system';
    const containsFinalTurn = indices.includes(lastUserIndex);
    groups.push({
      indices,
      tokens: indices.reduce((a, k) => a + costOf(k), 0),
      protectedReason: isSystem ? 'system' : containsFinalTurn ? 'final-turn' : null,
      retrieved: indices.some((k) => String(messages[k].role) === 'tool'),
      label:
        indices.length > 1
          ? `${String(m.role)} + ${indices.length - 1} tool result${indices.length > 2 ? 's' : ''}`
          : String(m.role ?? 'message'),
    });

    i += indices.length;
  }
  return groups;
}

function findLastIndex<T>(xs: T[], pred: (x: T) => boolean): number {
  for (let i = xs.length - 1; i >= 0; i--) if (pred(xs[i])) return i;
  return -1;
}

export interface PackPlan {
  /** Indices of messages to keep, ascending. */
  keep: number[];
  /** Indices dropped, in the order the packer chose to drop them. */
  dropped: number[];
  /** Estimated tokens after dropping, from the measured per-message costs. */
  estimate: number;
  /** True when everything droppable was dropped and it still does not fit. */
  impossible: boolean;
  /** Set when the system prompt had to be dropped — always surfaced loudly. */
  droppedSystem: boolean;
  notes: string[];
}

export interface PackOptions {
  messages: ChatMessage[];
  segments: Segment[];
  /** Tokens contributed by things that cannot be dropped by re-selecting
   *  messages — the tool definitions and the template's own overhead. */
  fixedTokens: number;
  budget: number;
  strategy?: PackStrategy;
  /** Allow the system prompt to be dropped as a last resort. */
  allowDropSystem?: boolean;
}

/**
 * Choose what to drop. Pure: no rendering, no tokenizing, no side effects —
 * which is what makes the ordering rules testable in isolation.
 */
export function planPack(opts: PackOptions): PackPlan {
  const { messages, segments, fixedTokens, budget, strategy = 'oldest-first', allowDropSystem = false } = opts;

  const costs = new Map<number, number>();
  for (const s of segments) if (s.messageIndex >= 0) costs.set(s.messageIndex, s.tokens);
  const costOf = (i: number) => costs.get(i) ?? 0;

  const groups = buildGroups(messages, costOf);
  const keep = new Set<number>(messages.map((_, i) => i));
  const dropped: number[] = [];
  const notes: string[] = [];

  let running = fixedTokens + groups.reduce((a, g) => a + g.tokens, 0);

  const droppable = groups.filter((g) => g.protectedReason === null);
  const order = orderGroups(droppable, strategy);

  for (const g of order) {
    if (running <= budget) break;
    for (const idx of g.indices) {
      keep.delete(idx);
      dropped.push(idx);
    }
    running -= g.tokens;
  }

  let droppedSystem = false;
  if (running > budget && allowDropSystem) {
    const sys = groups.find((g) => g.protectedReason === 'system');
    if (sys) {
      for (const idx of sys.indices) {
        keep.delete(idx);
        dropped.push(idx);
      }
      running -= sys.tokens;
      droppedSystem = true;
      notes.push('The system prompt was dropped to fit the budget. Expect the model to behave differently.');
    }
  }

  const impossible = running > budget;
  if (impossible) {
    notes.push(
      'Even with every droppable turn removed, the prompt does not fit. What is left — the system prompt, the tool definitions, the final turn and the template itself — is bigger than the budget.',
    );
  }
  if (dropped.length > 0 && !impossible) {
    notes.push(`Dropped ${dropped.length} message${dropped.length === 1 ? '' : 's'} to fit ${budget.toLocaleString()} tokens.`);
  }

  return {
    keep: [...keep].sort((a, b) => a - b),
    dropped,
    estimate: running,
    impossible,
    droppedSystem,
    notes,
  };
}

function orderGroups(groups: PackGroup[], strategy: PackStrategy): PackGroup[] {
  if (strategy === 'documents-first') {
    // Biggest retrieved blocks first — they are usually the cheapest thing to
    // lose per token saved — then fall back to oldest-first for the rest.
    const retrieved = groups.filter((g) => g.retrieved).sort((a, b) => b.tokens - a.tokens);
    const rest = groups.filter((g) => !g.retrieved);
    return [...retrieved, ...rest];
  }
  return [...groups];
}

/** Apply a plan, producing the trimmed conversation to export. */
export function applyPlan(messages: ChatMessage[], plan: PackPlan): ChatMessage[] {
  return plan.keep.map((i) => messages[i]);
}

/**
 * A packed conversation must still be sendable. This is the check that runs
 * against the *result*, not the plan, so a bug in the ordering rules cannot
 * quietly ship a broken export.
 */
export function validateConversation(messages: ChatMessage[]): string[] {
  const problems: string[] = [];
  const callIds = new Set<string>();

  for (const m of messages) {
    if (Array.isArray(m.tool_calls)) {
      for (const tc of m.tool_calls) if (tc.id) callIds.add(tc.id);
    }
  }
  for (const m of messages) {
    if (String(m.role) === 'tool') {
      const id = m.tool_call_id;
      if (typeof id === 'string' && id.length > 0 && !callIds.has(id)) {
        problems.push(`Orphaned tool result: ${id} has no matching tool call.`);
      }
    }
  }
  if (messages.length === 0) problems.push('Nothing left in the conversation.');
  else if (messages.every((m) => String(m.role) === 'system')) {
    problems.push('Only the system prompt is left — there is nothing for the model to answer.');
  }
  return problems;
}
